import styled from 'styled-components';
import {
  FontSizes,
  LinkFooter,
} from '../../common/typography/typography.styles';

export const FooterContainer = styled.div`
  width: 100%;
  @media (max-width: 900px) {
    margin-top: 0;
  }
`;

export const FooterTopContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 4rem;
  padding: 3.2rem 4rem;
  max-width: 144rem;
  margin: 0 auto;

  .footer-brand {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    flex: 0 0 18.3rem;
    gap: 2.4rem;
  }

  .footer-menu {
    display: flex;
    flex: 1 1 auto;
    flex-wrap: wrap;
    gap: 2.4rem;
    min-width: 0;
  }

  @media (max-width: 900px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 2rem;
    padding: 3.2rem 2.4rem;

    .footer-brand {
      display: block;
      order: 2;
      flex-basis: auto;
    }

    .footer-menu {
      flex-direction: column;
      gap: 4rem;
      order: 1;
    }
  }
`;

export const FooterContent = styled.div`
  width: 100%;
  background-color: ${({ theme }) => theme.colors.namedColors.white};

  &:last-child {
    border-top: 1px solid ${({ theme }) => theme.colors.baseColors.grey10};
  }
`;

export const FooterColumn = styled.div`
  width: 22rem;
  display: flex;
  flex-direction: column;
  gap: 1.2rem;

  ${LinkFooter} {
    display: block;

    &:link,
    &:visited {
      color: ${({ theme }) => theme.colors.baseColors.grey150};
      font-size: ${FontSizes.medium};
      font-weight: 500;
      line-height: 2.2rem;
    }
  }
`;

export const FooterColumnTitle = styled.h3`
  text-transform: uppercase;
  margin: 0;
  color: ${({ theme }) => theme.colors.footerColumnTitle.color};
  font-size: ${FontSizes.normal};
  font-weight: 500;
  line-height: 2rem;
  letter-spacing: 0.5px;
`;

export const FooterBottomContainer = styled.div`
  display: flex;
  gap: 2.4rem;
  justify-content: space-between;
  align-items: center;
  padding: 1.2rem 4rem;
  max-width: 144rem;
  margin: 0 auto;
  min-height: 4rem;

  span {
    color: ${({ theme }) => theme.colors.baseColors.grey100};
    font-weight: 400;
    width: auto;
  }

  @media (max-width: 900px) {
    flex-direction: column;
    justify-content: center;
    gap: 2.4rem;
    padding: 2.4rem;

    span {
      max-width: 32.7rem;
      text-align: center;
      white-space: normal;
    }
  }
`;

export const FooterLogo = styled.img`
  display: block;
  width: 16.1rem;
  height: auto;

  @media (max-width: 900px) {
    display: none;
  }
`;

export const FooterIconHolder = styled.div`
  display: flex;
  align-items: flex-end;
  width: 100%;
  margin-bottom: 8px;
  gap: 2px;

  h6 {
    font-family: 'Helvetica Neue', 'Helvetica', 'Arial', 'sans-serif';
    font-size: 19px;
    font-weight: 500;
    margin: 0;
    line-height: 19px;
  }
`;

export const FooterWidgetHolder = styled.div`
  .trustpilot-widget {
    width: 176px;
    margin-left: -12px;
  }
`;

export const SocialLinksContainer = styled.span<{ $isHidden?: boolean }>`
  display: flex;
  gap: 1.6rem;
  visibility: ${({ $isHidden }) => ($isHidden ? 'hidden' : 'visible')};

  a {
    display: block;
    width: 3.2rem;
    height: 3.2rem;
    padding: 0;

    svg {
      width: 100%;
      height: 100%;
    }
  }

  @media (max-width: 900px) {
    display: ${({ $isHidden }) => ($isHidden ? 'none' : 'flex')};

    a {
      width: 4rem;
      height: 4rem;
    }
  }
`;

interface StoreLinkProps {
  type: 'google' | 'apple';
}

export const StoreLink = styled.a<StoreLinkProps>`
  display: flex;
  align-items: center;

  svg {
    width: 100%;
    height: 100%;
  }

  @media (min-width: 651px) {
    width: 13.5rem;
    height: 4rem;
  }

  @media (max-width: 900px) {
    width: 21.6rem;
    height: 6.4rem;
  }
`;

interface Props {
  $hidden?: boolean;
}

export const AppLinksContainer = styled.span<Props>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1.2rem;
  visibility: hidden;

  visibility: ${({ $hidden }) => ($hidden ? 'hidden' : 'visible')};

  span {
    font-size: ${FontSizes.normal};
    font-weight: 500;
    line-height: 2rem;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: ${({ theme }) => theme.colors.namedColors.primary};
  }

  .store-links {
    display: flex;
    align-items: center;
    gap: 1.2rem;
  }

  @media (min-width: 900px) {
    justify-self: flex-end;
  }

  @media (max-width: 900px) {
    visibility: visible;
    display: ${({ $hidden }) => ($hidden ? 'none' : 'flex')};

    .store-links {
      flex-direction: column;
      align-items: flex-start;
    }
  }
`;

export const FooterActions = styled.div`
  display: flex;
  flex: 0 0 auto;
  flex-direction: column;
  align-items: flex-start;
  gap: 2.4rem;

  @media (max-width: 900px) {
    gap: 2.4rem;
    order: 3;
  }
`;

export const FooterLegalLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 1.6rem;

  a {
    color: ${({ theme }) => theme.colors.baseColors.grey100};
    font-size: ${FontSizes.small};
    font-weight: 500;
    line-height: 1.6rem;
    letter-spacing: 0.5px;
    text-decoration-line: underline;
    white-space: nowrap;
  }

  @media (max-width: 900px) {
    flex-direction: column;
    gap: 1.6rem;
  }
`;

export const ChangeCountrySelectTitle = styled.span`
  display: block;
  color: ${({ theme }) => theme.colors.baseColors.grey150};
  font-size: ${FontSizes.normal};
  line-height: 2rem;
  letter-spacing: 0.5px;
  margin-bottom: 1.2rem;
  word-break: break-word;
`;

export const BecomeAnAgentLink = styled.a`
  color: ${({ theme }) => theme.colors.namedColors.primary};
  padding: 0.8rem 0.4rem;
  font-size: 1.4rem;
  font-weight: 700;
  line-height: 1.6rem;
  letter-spacing: 0.05rem;
  text-decoration-line: underline;
  transition-property: border-color, background-color, color, outline-color;
  transition-duration: 0.2s;

  &:hover {
    color: ${({ theme }) => theme.colors.namedColors.secondaryPrimary};
  }

  &:active {
    color: ${({ theme }) => theme.colors.namedColors.main};
  }

  &:focus {
    color: ${({ theme }) => theme.colors.namedColors.secondaryPrimary};
    outline: none;
  }

  @media (max-width: 900px) {
    display: block;
    margin-top: 1.2rem;
  }
`;
